import React, { useState, useEffect, useRef } from 'react';
import {
  User,
  KeyRound,
  Eye,
  EyeOff,
  AlertCircle,
  X,
  Crown,
  Users,
  Smartphone,
  Tv,
  Laptop,
  ArrowRight,
  Trash2,
  AlertTriangle,
  Monitor,
  RefreshCw,
  LogOut,
  Download,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { APP_VERSION } from '../../version';
import { encryptCredential, decryptCredential } from '../../utils/security';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DeviceLimitSession {
  id: number;
  ip_address: string;
  device_name: string;
  created_at_formatted?: string;
  last_active_label?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { authUser, isAdmin, loginUser, logoutUser, showToast } = useIPTV();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deviceLimitSessions, setDeviceLimitSessions] = useState<DeviceLimitSession[] | null>(null);
  const [maxDevicesAllowed, setMaxDevicesAllowed] = useState<number>(5);
  const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null);
  const [isRevokingAll, setIsRevokingAll] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    return localStorage.getItem('opentv_remember_me') !== 'false';
  });

  const identifierInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
      setDeviceLimitSessions(null);
      try {
        const saved = localStorage.getItem('opentv_saved_cred');
        if (saved) {
          const dec = JSON.parse(decryptCredential(saved));
          if (dec && dec.identifier) {
            setIdentifier(dec.identifier);
            if (dec.password) setPassword(dec.password);
          }
        }
      } catch {
        // Ignora erro ao decodificar
      }
      setTimeout(() => {
        identifierInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setDeviceLimitSessions(null);

    const cleanIdentifier = identifier.trim();
    if (!cleanIdentifier || !password) {
      setErrorMessage('Por favor, preencha o usuário e a senha de acesso.');
      return;
    }

    setLoading(true);
    try {
      const success = await loginUser(cleanIdentifier, password);
      if (success) {
        if (rememberMe) {
          localStorage.setItem('opentv_remember_me', 'true');
          localStorage.setItem('opentv_saved_cred', encryptCredential(JSON.stringify({ identifier: cleanIdentifier, password })));
        } else {
          localStorage.setItem('opentv_remember_me', 'false');
          localStorage.removeItem('opentv_saved_cred');
        }
        showToast('Login realizado com sucesso! Bem-vindo.', 'success');
        onClose();
      } else {
        setErrorMessage('Usuário ou senha incorretos.');
      }
    } catch (err: any) {
      const data = err?.data || err?.response?.data;
      const isLimitReached = data?.device_limit_reached || (err.message && err.message.toLowerCase().includes('limite de'));

      if (isLimitReached) {
        setDeviceLimitSessions(Array.isArray(data?.active_sessions) ? data.active_sessions : []);
        setMaxDevicesAllowed(data?.max_devices || 5);
        setErrorMessage(null);
      } else {
        setErrorMessage(err.message || 'Erro de conexão com o servidor.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAndLogin = async (sessionId?: number, revokeAllOther = false) => {
    const cleanIdentifier = identifier.trim();
    if (!cleanIdentifier || !password) return;

    if (sessionId) {
      setRevokingSessionId(sessionId);
    } else if (revokeAllOther) {
      setIsRevokingAll(true);
    } else {
      setLoading(true);
    }
    setErrorMessage(null);

    try {
      const success = await loginUser(cleanIdentifier, password, {
        revoke_session_id: sessionId,
        revoke_all_other: revokeAllOther,
      });
      if (success) {
        if (rememberMe) {
          localStorage.setItem('opentv_remember_me', 'true');
          localStorage.setItem('opentv_saved_cred', encryptCredential(JSON.stringify({ identifier: cleanIdentifier, password })));
        } else {
          localStorage.setItem('opentv_remember_me', 'false');
          localStorage.removeItem('opentv_saved_cred');
        }
        showToast('Aparelho liberado e login realizado com sucesso! ⚡', 'success');
        setDeviceLimitSessions(null);
        onClose();
      } else {
        setErrorMessage('Não foi possível realizar o login.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro ao conectar este aparelho.');
    } finally {
      setRevokingSessionId(null);
      setIsRevokingAll(false);
      setLoading(false);
    }
  };

  const getDeviceIcon = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.includes('tv') || lower.includes('tizen') || lower.includes('webos')) {
      return <Tv className="w-5 h-5 text-amber-400" />;
    }
    if (lower.includes('android') || lower.includes('iphone') || lower.includes('celular') || lower.includes('mobile')) {
      return <Smartphone className="w-5 h-5 text-cyan-400" />;
    }
    return <Laptop className="w-5 h-5 text-indigo-400" />;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black"
    >
      <div
        className={`relative w-full ${
          deviceLimitSessions ? 'max-w-xl' : 'max-w-md'
        } bg-zinc-900 border border-amber-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden`}
      >
        {/* Close Button (apenas se já estiver autenticado) */}
        {authUser && (
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Header: Logo TV Dourada ao lado do texto OPEN TV */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <svg
            viewBox="65 60 382 360"
            className="w-12 h-12 sm:w-14 sm:h-14 drop-shadow-[0_0_25px_rgba(245,158,11,0.6)] shrink-0"
          >
            {/* Antenas */}
            <path
              d="M176 82L256 160L336 82"
              fill="none"
              stroke="#F59E0B"
              strokeWidth="32"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Moldura da TV */}
            <rect
              x="86"
              y="160"
              width="340"
              height="246"
              rx="42"
              fill="none"
              stroke="#F59E0B"
              strokeWidth="32"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Tela interna */}
            <rect
              x="114"
              y="188"
              width="284"
              height="190"
              rx="16"
              fill="#F59E0B"
              fillOpacity="0.2"
            />
          </svg>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              OPEN TV
            </h2>
            <span
              style={{
                backgroundColor: 'rgba(245, 158, 11, 0.18)',
                borderColor: 'rgba(245, 158, 11, 0.45)',
                color: '#fbbf24',
              }}
              className="px-2 py-0.5 rounded-lg border text-[10px] font-mono font-bold shadow-sm shadow-amber-500/20 select-none"
            >
              v{APP_VERSION}
            </span>
          </div>
        </div>

        {/* 1. ESTADO: LIMITE DE 5 APARELHOS ATINGIDO -> ESCOLHER QUAL DESCONECTAR */}
        {deviceLimitSessions ? (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs">
                <strong className="text-amber-300 block font-bold mb-0.5">
                  Limite de {maxDevicesAllowed} Aparelhos Atingido
                </strong>
                <span className="text-zinc-300">
                  Sua conta já possui {maxDevicesAllowed} aparelhos ativos. Escolha qual aparelho desconectar para entrar com este agora:
                </span>
              </div>
            </div>

            {errorMessage && (
              <div className="p-3 bg-red-500/15 border border-red-500/30 rounded-xl flex items-center gap-2 text-xs text-red-400">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Lista dos Aparelhos Ativos */}
            <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-zinc-800">
              {deviceLimitSessions.length === 0 ? (
                <div className="p-4 bg-zinc-950/70 border border-zinc-800 rounded-2xl text-center space-y-3">
                  <p className="text-xs text-zinc-300">
                    Sessões antigas foram registradas nesta conta. Clique no botão abaixo para liberar o acesso e conectar com este aparelho imediatamente:
                  </p>
                  <button
                    type="button"
                    disabled={loading || isRevokingAll}
                    onClick={() => handleRevokeAndLogin(undefined, true)}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-extrabold text-xs transition cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 active:scale-95 disabled:opacity-50 tracking-wide"
                  >
                    <span>{isRevokingAll ? 'Liberando e Conectando...' : 'Liberar e Entrar com este Aparelho'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                deviceLimitSessions.map((session) => (
                  <div
                    key={session.id}
                    className="p-3 bg-zinc-950/70 border border-zinc-800 rounded-2xl flex items-center justify-between gap-3 hover:border-zinc-700 transition"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 shrink-0">
                        {getDeviceIcon(session.device_name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-white truncate">
                          {session.device_name}
                        </div>
                        <div className="text-[11px] text-zinc-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                          <span>IP: {session.ip_address}</span>
                          {session.last_active_label && (
                            <span>• Ativo: <strong className="text-zinc-300">{session.last_active_label}</strong></span>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      disabled={revokingSessionId === session.id || isRevokingAll}
                      onClick={() => handleRevokeAndLogin(session.id)}
                      className="px-3 py-2 rounded-xl bg-amber-500/15 hover:bg-amber-500 text-amber-300 hover:text-black border border-amber-500/30 hover:border-amber-500 text-xs font-bold transition cursor-pointer shrink-0 flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
                      title="Desconectar este aparelho e entrar neste"
                    >
                      <span>{revokingSessionId === session.id ? 'Entrando...' : 'Desconectar e Entrar'}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Ações Inferiores */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-2.5">
              <button
                type="button"
                disabled={isRevokingAll || !!revokingSessionId}
                onClick={() => handleRevokeAndLogin(undefined, true)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-red-950/40 hover:bg-red-600 text-red-300 hover:text-white border border-red-500/30 hover:border-red-600 text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isRevokingAll ? 'Desconectando...' : 'Desconectar Todos os Outros e Entrar'}</span>
              </button>

              <button
                type="button"
                onClick={() => setDeviceLimitSessions(null)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-800 text-xs font-bold transition cursor-pointer"
              >
                Voltar
              </button>
            </div>
          </div>
        ) : authUser ? (
          /* 2. ESTADO LOGADO */
          <div className="space-y-4">
            <div className="bg-zinc-950/80 border border-white/10 rounded-2xl p-4 flex items-center gap-3.5">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-inner ${
                  isAdmin
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                }`}
              >
                {isAdmin ? <Crown className="w-6 h-6" /> : <User className="w-6 h-6" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white text-sm truncate">
                    {authUser.username}
                  </span>
                  {isAdmin && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full text-[10px] font-bold">
                      ADMIN
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-400 truncate mt-0.5">
                  {authUser.email || 'Conta IPTV Ativa'}
                </div>
              </div>
            </div>

            <button
              onClick={logoutUser}
              className="w-full py-3 bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 font-semibold rounded-2xl text-xs transition cursor-pointer"
            >
              Sair da Conta (Logout)
            </button>

            <button
              onClick={onClose}
              className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-2xl text-xs transition cursor-pointer"
            >
              Continuar Assistindo
            </button>
          </div>
        ) : (
          /* 3. FORMULÁRIO DE LOGIN NORMAL */
          <form onSubmit={handleSubmit} className="space-y-4">
            {errorMessage && (
              <div className="p-3 bg-red-500/15 border border-red-500/30 rounded-xl flex items-center gap-2 text-xs text-red-400 animate-shake">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold text-zinc-300 uppercase tracking-wider mb-1.5">
                Usuário ou E-mail
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-500">
                  <User className="w-4 h-4" />
                </div>
                <input
                  ref={identifierInputRef}
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Digite seu usuário ou e-mail"
                  className="w-full pl-10 pr-4 py-2.5 bg-black/50 border border-white/20 focus:border-white focus:ring-2 focus:ring-white/20 rounded-xl text-white text-xs outline-none transition placeholder:text-zinc-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-zinc-300 uppercase tracking-wider mb-1.5">
                Senha de Acesso
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-500">
                  <KeyRound className="w-4 h-4" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  name="open_tv_auth_key"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha de acesso"
                  className="w-full pl-10 pr-10 py-2.5 bg-black/50 border border-white/20 focus:border-white focus:ring-2 focus:ring-white/20 rounded-xl text-white text-xs outline-none transition placeholder:text-zinc-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-zinc-400 hover:text-white transition cursor-pointer"
                  title={showPassword ? 'Ocultar senha' : 'Ver senha'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Lembrar-me */}
            <div className="flex items-center justify-between text-xs px-0.5">
              <label className="flex items-center gap-2 cursor-pointer select-none text-zinc-400 hover:text-white transition">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded bg-black/50 border-white/20 text-amber-500 focus:ring-amber-400 focus:ring-offset-0 transition cursor-pointer accent-amber-500"
                />
                <span>Lembrar meus dados neste aparelho</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-extrabold rounded-2xl text-xs transition cursor-pointer shadow-lg shadow-amber-500/30 flex items-center justify-center gap-2 disabled:opacity-50 tracking-wider"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                'ENTRAR'
              )}
            </button>

            {/* Link para Baixar APK Android */}
            <div className="pt-2 border-t border-white/5 flex items-center justify-center">
              <a
                href="/app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-bold text-amber-400/90 hover:text-amber-300 flex items-center gap-1.5 transition py-1.5 px-3 rounded-xl hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20"
                title="Baixar aplicativo instalável para celular Android ou TV Box"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Baixar Aplicativo Android / TV Box (APK)</span>
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
